import { Capacitor } from "@capacitor/core";
import { resolveMenuMotionTier, type MenuMotionTier } from "./menu-motion-budget";

export const LEGACY_ANDROID_WEBVIEW_MAX_MAJOR = 99;

export interface ConstrainedCompositorHints {
  nativePlatform: boolean;
  platform: string;
  userAgent: string;
  logicalProcessors?: number;
  deviceMemoryGb?: number;
}

interface CompositorDataset {
  constrainedCompositor?: string;
  menuMotionTier?: MenuMotionTier;
}

interface CompositorRoot {
  dataset: CompositorDataset;
}

interface NavigatorCompositorHints extends Navigator {
  deviceMemory?: number;
}

export const readChromiumMajor = (userAgent: string): number | undefined => {
  const match = /\b(?:Chrome|Chromium)\/(\d+)/i.exec(userAgent);
  if (!match) return undefined;
  const major = Number(match[1]);
  return Number.isInteger(major) && major > 0 ? major : undefined;
};

const isPositiveFiniteAtMost = (value: number | undefined, maximum: number): boolean => (
  typeof value === "number" && Number.isFinite(value) && value > 0 && value <= maximum
);

/**
 * Compositor safety is intentionally independent from the player's graphics
 * preference. It removes expensive DOM effects only for a native Android
 * WebView with a legacy Chromium compositor or clearly constrained hardware;
 * it never lowers the arena's resolution or device pixel ratio.
 */
export const isConstrainedCompositor = (hints: ConstrainedCompositorHints): boolean => {
  if (!hints.nativePlatform || hints.platform !== "android") return false;
  const chromiumMajor = readChromiumMajor(hints.userAgent);
  const legacyWebView = chromiumMajor !== undefined && chromiumMajor <= LEGACY_ANDROID_WEBVIEW_MAX_MAJOR;
  const lowCpu = isPositiveFiniteAtMost(hints.logicalProcessors, 4);
  const lowMemory = isPositiveFiniteAtMost(hints.deviceMemoryGb, 4);
  return legacyWebView || lowCpu || lowMemory;
};

export const applyConstrainedCompositor = (root: CompositorRoot, hints: ConstrainedCompositorHints): boolean => {
  const constrained = isConstrainedCompositor(hints);
  root.dataset.menuMotionTier = resolveMenuMotionTier(hints);
  if (constrained) root.dataset.constrainedCompositor = "true";
  else delete root.dataset.constrainedCompositor;
  return constrained;
};

/** Call once before the first React render so the initial WebView paint is safe. */
export const initializeConstrainedCompositor = (): boolean => {
  if (typeof document === "undefined" || typeof navigator === "undefined") return false;
  const browser = navigator as NavigatorCompositorHints;
  return applyConstrainedCompositor(document.documentElement, {
    nativePlatform: Capacitor.isNativePlatform(),
    platform: Capacitor.getPlatform(),
    userAgent: browser.userAgent,
    logicalProcessors: browser.hardwareConcurrency,
    ...(typeof browser.deviceMemory === "number" ? { deviceMemoryGb: browser.deviceMemory } : {}),
  });
};
